import { useState, type ReactNode } from 'react';

import { useAppUpdatePrompt } from '../hooks/useAppUpdatePrompt';
import { type AppUpdatePrompt } from '../lib/appUpdateDecision';
import { isAppUpdateDismissed, recordAppUpdateDismissal } from '../lib/appUpdateDismissal';
import { AppUpdateScreen } from './AppUpdateScreen';

const DismissibleAppUpdateScreen = ({ prompt }: { prompt: AppUpdatePrompt }) => {
	const remembersDismissal = !prompt.forced && !prompt.simulated;
	const [dismissed, setDismissed] = useState(
		() => remembersDismissal && isAppUpdateDismissed(prompt),
	);
	if (dismissed) return null;
	const handleClose = () => {
		if (remembersDismissal) recordAppUpdateDismissal(prompt);
		setDismissed(true);
	};
	return <AppUpdateScreen prompt={prompt} onClose={handleClose} />;
};

/**
 * Gate of the `app-update-wall` flag over the whole app, mounted inside `MaintenanceGate`
 * with the same reach: no page is excluded, login included.
 * Required mode replaces `children`; optional mode overlays them with a closable view,
 * so closing it reveals the app without remounting. A prompt from the browser simulation
 * is always the closable overlay, so a real browser can never be blocked.
 * Without the flag, outside the app, or on an up-to-date version it renders `children` untouched.
 */
export const AppUpdateGate = ({ children }: { children: ReactNode }) => {
	const prompt = useAppUpdatePrompt();
	if (prompt?.mode === 'required' && !prompt.simulated) return <AppUpdateScreen prompt={prompt} />;
	return (
		<>
			{children}
			{prompt && <DismissibleAppUpdateScreen prompt={prompt} />}
		</>
	);
};
